import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMessageHooks } from '../Hooks/MessageHooks';
import { getLoggedinUser } from '../../helpers/api_helper';
import config from '../../config';
import './QuickMessageSpotlight.css';

interface QuickMessageSpotlightProps {
  isOpen: boolean;
  onClose: () => void;
  initialUser?: { id: string; full_name: string; email: string; };
}

const QuickMessageSpotlight: React.FC<QuickMessageSpotlightProps> = ({ isOpen, onClose, initialUser }) => {
  const [message, setMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [sending, setSending] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const currentUser = getLoggedinUser();
  const { groups, users, actions } = useMessageHooks(currentUser?.user?.id);
  
  // Filter users excluding current user
  const filteredUsers = users.filter(user => 
    user.id !== currentUser?.user?.id
  ).slice(0, 5); // Limit to 5 suggestions

  // Extract @ mention from message
  const extractMention = useCallback(() => {
    const match = message.match(/@(\w*)$/);
    return match ? match[1] : null;
  }, [message]);

  // Define handleClose early with useCallback
  const handleClose = useCallback(() => {
    setMessage('');
    setSelectedUser(null);
    setShowSuggestions(false);
    setSelectedIndex(0);
    onClose();
  }, [onClose]);

  // Search users when @ is typed
  useEffect(() => {
    const mention = extractMention();
    if (mention !== null) {
      actions.searchUsers(mention);
      if (!showSuggestions) {
        // Only reset index when suggestions are first shown
        setSelectedIndex(0);
      }
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSelectedIndex(0);
    }
  }, [message, extractMention, actions, showSuggestions]);

  // Initialize with initial user if provided
  useEffect(() => {
    if (isOpen && initialUser) {
      setSelectedUser(initialUser);
      setMessage(`@${initialUser.full_name} `);
    }
  }, [isOpen, initialUser]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Global ESC key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isOpen, handleClose]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        selectUser(filteredUsers[selectedIndex]);
      }
    } else if (e.key === 'Enter' && !e.shiftKey && selectedUser) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const selectUser = (user: any) => {
    const newMessage = message.replace(/@\w*$/, `@${user.name} `);
    setMessage(newMessage);
    setSelectedUser(user);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!selectedUser || !message.trim()) return;

    setSending(true);
    try {
      // First, check if a direct message group already exists
      await actions.fetchGroups();
      
      // Find existing direct message group with this user
      let groupId = null;
      const existingGroup = groups.find(group => 
        group.is_direct_message && 
        group.name === selectedUser.name
      );
      
      if (existingGroup) {
        groupId = existingGroup.id;
      } else {
        // Create a new direct message group
        const response = await fetch(
          `${config.api.API_URL}/messages/groups`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              name: selectedUser.name, 
              description: '', 
              memberIds: [selectedUser.id] 
            })
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to create group');
        }
        
        const data = await response.json();
        if (data.success && data.data) {
          groupId = data.data.id;
        }
      }
      
      if (groupId) {
        // Remove @ mention from message - match the full name including spaces
        const messageContent = message.replace(new RegExp(`@${selectedUser.name}\\s*`), '').trim();
        
        // Send the message
        const sent = await actions.sendMessage(groupId, messageContent);
        
        if (sent) {
          // Close the spotlight and stay on current page
          handleClose();
        }
      }
    } catch (error) {
      console.error('Failed to send quick message:', error);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quick-message-overlay" onClick={handleClose}>
      <div className="quick-message-spotlight" onClick={(e) => e.stopPropagation()}>
        <div className="quick-message-header">
          <h4>Quick Message</h4>
          <button className="close-button" onClick={handleClose}>
            <i className="ri-close-line"></i>
          </button>
        </div>
        
        <div className="quick-message-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="quick-message-input"
            placeholder="Type @ to mention someone..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
          />
          
          {selectedUser && (
            <div className="selected-user-chip">
              <span>To: {selectedUser.name}</span>
              <button onClick={() => {
                setSelectedUser(null);
                setMessage('');
                inputRef.current?.focus();
              }}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}
        </div>

        {showSuggestions && filteredUsers.length > 0 && (
          <div className="suggestions-dropdown">
            {filteredUsers.map((user, index) => (
              <div
                key={user.id}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => selectUser(user)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="user-avatar">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="user-info">
                  <div className="user-name">{user.name}</div>
                  <div className="user-email">{user.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="quick-message-footer">
          <div className="shortcuts-hint">
            <span><kbd>Enter</kbd> to send</span>
            <span><kbd>Esc</kbd> to close</span>
          </div>
          {selectedUser && (
            <button 
              className="send-button"
              onClick={handleSend}
              disabled={!message.trim() || sending}
            >
              {sending ? (
                <>
                  <i className="ri-loader-4-line ri-spin"></i>
                  Sending...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  Send
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickMessageSpotlight;